import { ENTITY_ICONS } from "@/constants/entity-icons"
import { SOFIA_PRODUCT_NAME } from "@/constants/sofia"
import { CREATE_TEMPLATE_INTENT_PATH } from "@/templates/navigation"
import {
  Activity,
  Bell,
  BotMessageSquare,
  ClipboardCheck,
  FileText,
  Gauge,
  Home,
  LineChart,
  Mic,
  Puzzle,
  RefreshCw,
  Ruler,
  Search,
  SlidersHorizontal,
  Volume2,
  Workflow,
} from "lucide-react"

import { CovalMark } from "@/components/icons/CovalLogo"
import { MessageSquareArrowUp } from "@/components/icons/MessageSquareArrowUp"

import type { NavigationConfigItem } from "../types"

export const HOME_BASE_HREF = "/home"

/**
 * The Uploaded page has no create route: uploading happens in a dialog on the
 * page itself, which this query parameter pops open on arrival.
 */
const UPLOAD_CONVERSATION_BASE_HREF = "/uploaded?upload=true"

/**
 * How the Home entry reads: Home is a Sofia surface, so the sidebar entry and
 * its `g h` command name it that, with the Coval mark in place of the house
 * glyph. Search keywords keep the old vocabulary so
 * "home" and "dashboard" still find it.
 *
 * Applied by `buildAppShellNavigationItems` and `useAvailableCommands` rather
 * than baked into the config below, whose label still feeds command
 * `pathLabels` (and therefore the palette's "home" alias).
 */
export const SOFIA_HOME_NAV_OVERRIDE = {
  label: SOFIA_PRODUCT_NAME,
  icon: CovalMark,
  keywords: ["sofia", "home", "dashboard", "overview", "coval"],
} as const

/**
 * The sidebar, grouped around the agent improvement loop: the conversations a
 * change produced, what judges them, explore what they did, configure what runs
 * next. The first section is named for the material it holds, and its children
 * for where those conversations came from: simulated by Coval, or uploaded from
 * production. The second holds the two ways a conversation gets judged,
 * automatically by a metric or by a person.
 *
 * Sections are grouping labels rather than links: `isSection` marks them so the
 * builder keeps a section whose own href is out of reach while its children are
 * not, and so a section can host children that are themselves owned elsewhere
 * (Dashboards brings its own).
 *
 * The compact sidebar renders one icon per top-level item and no children, so a
 * section's own `base_href` is where its icon leads: "Conversations" opens
 * Simulated, and the rest of its children are reachable only once the sidebar
 * expands.
 */
export const siteNavigationConfig: {
  navItems: NavigationConfigItem[]
  navMenuItems: NavigationConfigItem[]
} = {
  navItems: [
    {
      label: "Home",
      base_href: HOME_BASE_HREF,
      icon: Home,
      keywords: ["home", "dashboard", "overview", "coval"],
    },
    {
      label: "Conversations",
      base_href: "/simulated",
      icon: Workflow,
      isSection: true,
      keywords: [
        "conversations",
        "simulate",
        "observe",
        "upload",
        "transcripts",
        "loop",
      ],
      subItems: [
        {
          label: "Simulated",
          base_href: "/simulated",
          createBaseHref: "/simulate",
          icon: BotMessageSquare,
          keywords: [
            "simulated",
            "view",
            "runs",
            "simulations",
            "simulate",
            "conversations",
          ],
        },
        {
          label: "Uploaded",
          base_href: "/uploaded",
          createBaseHref: UPLOAD_CONVERSATION_BASE_HREF,
          createLabel: "Upload conversation",
          icon: MessageSquareArrowUp,
          keywords: [
            "uploaded",
            "conversations",
            "live",
            "observe",
            "observability",
            "monitoring",
          ],
        },
      ],
    },
    {
      label: "Measure",
      base_href: "/metrics",
      icon: Ruler,
      isSection: true,
      keywords: ["measure", "metrics", "evaluate", "review", "score", "judge"],
      subItems: [
        {
          label: "Metrics",
          base_href: "/metrics",
          createBaseHref: "/metrics?create=true",
          icon: ENTITY_ICONS.metrics,
          keywords: [
            "metrics",
            "workflows",
            "tool call",
            "binary",
            "judge",
            "llm",
          ],
        },
        {
          label: "Human Review",
          base_href: "/review/projects",
          createBaseHref: "/review/projects?create=true",
          icon: ClipboardCheck,
          keywords: ["review", "human", "feedback", "runs"],
        },
      ],
    },
    {
      label: "Explore",
      base_href: "/dashboard",
      icon: LineChart,
      isSection: true,
      keywords: ["explore", "dashboards", "reports", "traces"],
      subItems: [
        {
          label: "Dashboards",
          base_href: "/dashboard",
          icon: LineChart,
          keywords: ["simulations", "conversations", "dashboard"],
        },
        {
          label: "Reports",
          base_href: "/reports",
          icon: FileText,
          keywords: ["reports", "saved", "multi-run"],
        },
        {
          label: "Trace Search",
          base_href: "/traces",
          icon: Search,
          keywords: ["traces", "search", "otel", "opentelemetry", "spans"],
        },
      ],
    },
    {
      label: "Configure",
      base_href: "/agents",
      icon: SlidersHorizontal,
      isSection: true,
      keywords: ["agents", "personas", "test sets", "alerts"],
      subItems: [
        {
          label: "Agents",
          base_href: "/agents",
          createBaseHref: "/agents/create",
          icon: ENTITY_ICONS.agents,
          keywords: ["agents"],
        },
        {
          label: "Personas",
          base_href: "/personas",
          createBaseHref: "/personas/new",
          icon: ENTITY_ICONS.personas,
          keywords: ["personas", "simulators", "voice ids"],
        },
        {
          label: "Test Sets",
          base_href: "/test-sets",
          createBaseHref: "/test-sets/create",
          icon: ENTITY_ICONS["test-sets"],
          keywords: ["test", "evaluate", "coval", "datasets"],
        },
        {
          label: "Templates",
          base_href: "/templates",
          createBaseHref: CREATE_TEMPLATE_INTENT_PATH,
          icon: Puzzle,
          keywords: ["templates", "test", "evaluate", "coval"],
        },
        {
          label: "Scheduled",
          base_href: "/scheduled",
          createBaseHref: "/simulate",
          icon: RefreshCw,
          keywords: ["scheduled", "runs"],
        },
        {
          label: "Alerts",
          base_href: "/alerts",
          createBaseHref: "/alerts/create",
          icon: Bell,
          keywords: ["alerts", "monitors", "thresholds", "notifications"],
        },
      ],
    },
    {
      label: "Benchmarks",
      base_href: "/benchmarks",
      icon: Gauge,
      isSection: true,
      featureFlag: "benchmarks",
      keywords: ["benchmark", "stt", "tts", "performance", "wer", "ttfc"],
      subItems: [
        {
          label: "STT",
          base_href: "/benchmarks/stt",
          icon: Mic,
          keywords: ["stt", "speech to text", "transcription", "wer"],
        },
        {
          label: "TTS",
          base_href: "/benchmarks/tts",
          icon: Volume2,
          keywords: ["tts", "text to speech", "synthesis", "ttfc"],
        },
      ],
    },
  ],
  navMenuItems: [
    {
      label: "Test Sets",
      base_href: "/test-sets",
      icon: ENTITY_ICONS["test-sets"],
    },
    {
      label: "Simulated",
      base_href: "/simulated",
      icon: BotMessageSquare,
    },
    {
      label: "Uploaded",
      base_href: "/uploaded",
      icon: Activity,
    },
  ],
}
